// The interface language follows the browser: Russian for a Russian-language
// browser, English for everything else. Decided once at load, no switcher.
// Wording is shared with the mobile app's src/i18n - keep the two in step.
const browserLanguage = (
  navigator.languages?.[0] ||
  navigator.language ||
  "en"
).toLowerCase();

export const lang: "en" | "ru" = browserLanguage.startsWith("ru") ? "ru" : "en";
export const locale = lang === "ru" ? "ru-RU" : "en-US";

const en = {
  pageTitle: "ToDoo - Personal Planner",
  loading: "Loading...",
  refreshing: "Refreshing...",
  somethingWentWrong: "Something went wrong",
  noConnection: "No connection - please try again",
  sessionExpired: "Your session has expired - please log in again",

  email: "Email",
  password: "Password",
  logIn: "Log In",
  sendResetLink: "Send Link",
  forgotPassword: "Forgot your password?",
  backToLogIn: "Log In / Sign Up",
  resetLinkSent: "A password reset link has been sent to your email",
  enterCredentials: "Enter your email and password",
  enterValidEmail: "Enter a valid email",
  // Firebase Auth REST error codes
  authErrors: {
    EMAIL_NOT_FOUND: "No account found with this email",
    INVALID_PASSWORD: "Incorrect password",
    INVALID_LOGIN_CREDENTIALS: "Incorrect email or password",
    USER_DISABLED: "This account has been disabled",
    TOO_MANY_ATTEMPTS_TRY_LATER: "Too many attempts - try again in a few minutes",
  } as Record<string, string>,

  addNewEvent: "Add New Event",
  save: "Save",
  weekdaysShort: ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"],

  quickAdd: "Quick Add",
  important: "Important",
  add: "Add",
  enterEventTitle: "Enter an event title",
  checkFormFields: "Check the form fields",
  timeTillBeforeFrom: "Time Till must be later than Time From",

  selectEventHint: "Select an event on the left to view or edit it",
  date: "Date",
  repeatTitle: "Repeat",
  repeat: {
    never: "Don't repeat",
    weekly: "Every week",
    biweekly: "Every 2 weeks",
    triweekly: "Every 3 weeks",
    monthly: "Every month",
    yearly: "Every year",
  },
  title: "Title",
  time: "Time",
  timeFrom: "Time From",
  timeTill: "Time Till",
  clear: "Clear",
  done: "Done",
  hours: "Hours",
  minutes: "Minutes",
  birthday: "Birthday",
  remind: "Remind",
  description: "Description",
  saveChanges: "Save changes",

  eventCreated: "Event was created",
  eventUpdated: "Event was updated",
  eventCompleted: "Event was completed",

  // Every answer names its own outcome, so none of them can be read as
  // "cancel" the other way round.
  unsavedChanges: {
    title: "Save changes?",
    message: "You changed this event but haven't saved it.",
    noTitleTitle: "Close without saving?",
    noTitleMessage: "The new event needs a title before it can be saved.",
    save: "Save",
    discard: "Don't save",
    keepEditing: "Keep editing",
  },

  cancel: "Cancel",
  complete: {
    title: "Complete this event?",
    message: (title: string) => `"${title}" will move to your completed events.`,
    confirm: "Complete",
    birthdayTitle: "Dismiss birthday?",
    birthdayMessage: (title: string) => `Roll "${title}" to next year.`,
    birthdayConfirm: "Dismiss",
    repeatMessage: (title: string) =>
      `"${title}" repeats, so it will move to its next occurrence instead of being deleted.`,
    repeatWithTimeMessage: (title: string) =>
      `"${title}" repeats, so it will move to its next occurrence instead of being deleted, and its time will be cleared.`,
    repeatConfirm: "Advance",
  },
};

const ru: typeof en = {
  pageTitle: "ToDoo - Персональный ежедневник",
  loading: "Загрузка...",
  refreshing: "Обновление данных...",
  somethingWentWrong: "Что-то пошло не так",
  noConnection: "Нет соединения, попробуйте ещё раз",
  sessionExpired: "Сессия истекла, войдите снова",

  email: "E-mail",
  password: "Пароль",
  logIn: "Войти",
  sendResetLink: "Сбросить пароль",
  forgotPassword: "Забыли пароль?",
  backToLogIn: "Вход / Регистрация",
  resetLinkSent: "Информация отправлена на ваш e-mail",
  enterCredentials: "Укажите данные для входа",
  enterValidEmail: "Укажите корректный e-mail",
  authErrors: {
    EMAIL_NOT_FOUND: "Пользователь с таким e-mail не найден",
    INVALID_PASSWORD: "Неверный пароль",
    INVALID_LOGIN_CREDENTIALS: "Неверный e-mail или пароль",
    USER_DISABLED: "Пользователь был удален",
    TOO_MANY_ATTEMPTS_TRY_LATER:
      "Вы превысили число попыток входа. Попробуйте через несколько минут",
  },

  addNewEvent: "Добавить новое событие",
  save: "Сохранить",
  weekdaysShort: ["ПН", "ВТ", "СР", "ЧТ", "ПТ", "СБ", "ВС"],

  quickAdd: "Быстрое добавление",
  important: "Важное",
  add: "Добавить",
  enterEventTitle: "Введите название события",
  checkFormFields: "Проверьте данные формы",
  timeTillBeforeFrom: "«Время по» должно быть позже «Время с»",

  selectEventHint:
    "Выберите одно из событий слева для редактирования или просмотра его свойств",
  date: "Дата",
  repeatTitle: "Повторять",
  repeat: {
    never: "Не повторять",
    weekly: "Каждую неделю",
    biweekly: "Каждые 2 недели",
    triweekly: "Каждые 3 недели",
    monthly: "Каждый месяц",
    yearly: "Каждый год",
  },
  title: "Заголовок",
  time: "Время",
  timeFrom: "Время с",
  timeTill: "Время по",
  clear: "Очистить",
  done: "Готово",
  hours: "Часы",
  minutes: "Минуты",
  birthday: "День рождения",
  remind: "Напомнить",
  description: "Описание",
  saveChanges: "Сохранить изменения",

  eventCreated: "Событие создано",
  eventUpdated: "Событие обновлено",
  eventCompleted: "Событие завершено",

  unsavedChanges: {
    title: "Сохранить изменения?",
    message: "Вы изменили событие, но не сохранили его.",
    noTitleTitle: "Закрыть без сохранения?",
    noTitleMessage: "Чтобы сохранить новое событие, добавьте заголовок.",
    save: "Сохранить",
    discard: "Не сохранять",
    keepEditing: "Продолжить редактирование",
  },

  cancel: "Отмена",
  complete: {
    title: "Завершить это событие?",
    message: (title: string) => `«${title}» будет перемещено в завершённые события.`,
    confirm: "Завершить",
    birthdayTitle: "Перенести день рождения?",
    birthdayMessage: (title: string) => `«${title}» будет перенесено на следующий год.`,
    birthdayConfirm: "Перенести",
    repeatMessage: (title: string) =>
      `«${title}» повторяется, поэтому оно будет перенесено на следующую дату, а не удалено.`,
    repeatWithTimeMessage: (title: string) =>
      `«${title}» повторяется, поэтому оно будет перенесено на следующую дату, а не удалено, а его время будет очищено.`,
    repeatConfirm: "Перенести",
  },
};

export const t = lang === "ru" ? ru : en;
